// =============================================================================
// components/create/CreateSwipeHint.tsx — the BRIEF, self-dismissing create-swipe
// discoverability hint.
//
// Replaces the old permanently visible right-edge grab-handle ("light bubble" —
// client-rejected). Policy, enforced by CreateSwipeGesture via AsyncStorage:
// this hint appears on the FIRST TWO Home visits per install, floats in from the
// right edge, holds ~2.6s, fades out and unmounts. After that there is NO
// permanent create chrome — exactly like Instagram's handle-less camera swipe.
//
// Tapping the hint opens the CreateHub directly (a no-gesture fallback while the
// hint is on screen). It is never rendered again once the counter passes 2.
// =============================================================================
import React, { useEffect } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { glassTokens } from '../glass/tokens';
import { Ionicons } from '../ui/Icon';
import { colors, fonts, spacing } from '../../constants/theme';

export interface CreateSwipeHintProps {
  /** Tap fallback — opens the CreateHub. */
  onOpen: () => void;
  /** Fired after the fade-out completes → host unmounts the hint. */
  onDone: () => void;
}

// How long the hint stays fully visible before fading away.
const HOLD_MS = 2600;
const FADE_MS = 320;

export default function CreateSwipeHint({ onOpen, onDone }: CreateSwipeHintProps) {
  // 0 hidden → 1 shown. Drives both opacity and the slide-in from the edge.
  const shown = useSharedValue(0);

  useEffect(() => {
    shown.value = withTiming(1, { duration: FADE_MS, easing: Easing.out(Easing.cubic) });
    const t = setTimeout(() => {
      shown.value = withTiming(
        0,
        { duration: FADE_MS, easing: Easing.in(Easing.cubic) },
        (finished) => {
          if (finished) runOnJS(onDone)();
        },
      );
    }, HOLD_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = useAnimatedStyle(() => ({
    opacity: shown.value,
    transform: [{ translateX: (1 - shown.value) * 24 }],
  }));

  return (
    <Animated.View pointerEvents="box-none" style={[styles.host, style]}>
      <Pressable
        onPress={onOpen}
        hitSlop={12}
        accessibilityRole="button"
        accessibilityLabel="Create"
        accessibilityHint="Swipe left from the right edge to create"
        style={styles.pill}
      >
        <Ionicons name="chevron-back" size={16} color={colors.primary} />
        <Text style={styles.text}>Swipe to create</Text>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  // A one-off glass pill hugging the edge (right corners squared into the edge).
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minHeight: 44,
    paddingLeft: spacing.md,
    paddingRight: spacing.sm,
    borderTopLeftRadius: glassTokens.radius.pill,
    borderBottomLeftRadius: glassTokens.radius.pill,
    backgroundColor: glassTokens.surface.bar,
    borderWidth: 1,
    borderRightWidth: 0,
    borderColor: glassTokens.border,
    shadowColor: glassTokens.shadow.color,
    shadowOffset: glassTokens.shadow.offset,
    shadowOpacity: glassTokens.shadow.opacity,
    shadowRadius: glassTokens.shadow.radius,
    elevation: glassTokens.shadow.elevation,
  },
  text: {
    ...fonts.semibold,
    fontSize: 12,
    color: colors.ink,
  },
});
